import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Flex
} from '@chakra-ui/react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { chatService } from '../services/database';
import { apiService } from '../services/api';
import { v4 as uuidv4 } from 'uuid';
import { forceVersionClear } from '../utils/versionUtils';
import { tracking } from '../utils/tracking';
import ProductRecommendations from './ProductRecommendations';
import Preferences from './Preferences';
import IntentChipsScreen from './IntentChipsScreen';
import ChatBudgetHeader from './ChatBudgetHeader';
import ChatWelcome from './ChatWelcome';
import ChatInput from './ChatInput';
import DotLoader from './DotLoader';

const ChatBubble = ({ message, productData = null }) => {
  const isUser = message.role === 'user';
  

  return (
    <Flex
      justify={isUser ? 'flex-end' : 'flex-start'}
      mb={isUser ? 4 : 2}
      pl={isUser ? 4 : 2}
      pr={isUser ? 2 : 4}
    >
      <VStack align="stretch" spacing={0} width="100%">
        {/* Text Message - Left/Right Aligned Based on User */}
        <HStack justify={isUser ? 'flex-end' : 'flex-start'}>
          <Box
            bg={isUser ? 'blue.500' : 'transparent'}
            color={isUser ? 'white' : 'gray.800'}
            px={isUser ? 4 : 0}
            py={isUser ? 3 : 0}
            borderRadius={isUser ? "2xl" : "none"}
            boxShadow={isUser ? "sm" : "none"}
            border="none"
            maxW={isUser ? "70%" : "80%"}
          >
            {message.content?.startsWith('data:image') ? (
              <img
                src={message.content}
                alt="Uploaded outfit"
                style={{
                  maxWidth: '200px',
                  borderRadius: '8px',
                  display: 'block'
                }}
              />
            ) : message.content === '___LOADING___' ? (
              <DotLoader />
            ) : (
              <Text fontSize="md" lineHeight="tall" fontFamily="Inter, system-ui, -apple-system, sans-serif">
                {message.content}
              </Text>
            )}
          </Box>
        </HStack>
        
        {/* Product Recommendations - Always Centered, Full Width */}
        {!isUser && productData && productData.response_type !== 'GENERAL' && (
          <HStack justify="center" width="100%">
            <Box borderRadius="2xl" overflow="hidden" bg="purple.100" boxShadow="2xl">
              <ProductRecommendations
                productSearches={productData.data?.sections || productData.product_searches || []}
                complementaryItems={productData.data?.complementary_items || productData.complementary_items || []}
                isLoading={false}
              />
            </Box>
          </HStack>
        )}
      </VStack>
    </Flex>
  );
};

const ChatInterface = () => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [budget, setBudget] = useState(null);
  const [stylePreference, setStylePreference] = useState('women');
  const [showBudgetInput, setShowBudgetInput] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [chatUuid, setChatUuid] = useState(() => {
    // Try to restore session UUID from localStorage
    const savedUuid = localStorage.getItem('currentChatUuid');
    return savedUuid || uuidv4();
  }); // Session UUID
  const [isClearing, setIsClearing] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [lastResponse, setLastResponse] = useState(null);
  const [selectedGender, setSelectedGender] = useState(null); // 'women', 'men', or null
  const [showIntentChips, setShowIntentChips] = useState(false);
  const [intentChipData, setIntentChipData] = useState(null);
  const fileInputRef = useRef();
  const messagesEndRef = useRef();
  const messageAreaRef = useRef();
  // Removed toast - using simple error handling

  const loadMessages = useCallback(async () => {
    try {
      console.log('Loading messages for session:', chatUuid);
      const loadedMessages = await chatService.getMessages(chatUuid);
      console.log('Loaded messages:', loadedMessages);
      setMessages(loadedMessages);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  }, [chatUuid]);

  useEffect(() => {
    // Save current session UUID to localStorage
    localStorage.setItem('currentChatUuid', chatUuid);
    loadMessages();
  }, [chatUuid, loadMessages]); // Reload messages when session changes

  useEffect(() => {
    // Only scroll if there are messages
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  useEffect(() => {
    const messageArea = messageAreaRef.current;
    if (!messageArea) return;

    const handleScroll = (e) => {
      const { scrollTop, scrollHeight, clientHeight } = messageArea;
      const isAtBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 1;
      
      if (isAtBottom) {
        // Allow the scroll event to continue to the main body
        // by preventing it from being contained within the message area
        const wheelEvent = e.originalEvent || e;
        if (wheelEvent && wheelEvent.deltaY > 0) {
          // Scrolling down - continue to body scroll
          e.preventDefault();
          window.scrollBy(0, wheelEvent.deltaY);
        }
      }
    };

    messageArea.addEventListener('wheel', handleScroll, { passive: false });

    return () => {
      messageArea.removeEventListener('wheel', handleScroll);
    };
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const addMessage = async (role, content, sessionUuid = null, responseData = null) => {
    try {
      const uuid = sessionUuid || chatUuid;
      const message = await chatService.addMessage(role, content, uuid, responseData);
      setMessages(prev => [...prev, message]);
      return message;
    } catch (error) {
      console.error('Error adding message:', error);
      
      // Create a temporary message with timestamp for display
      const tempMessage = {
        id: Date.now(), // temporary ID
        session_uuid: sessionUuid || chatUuid,
        message_index: messages.length,
        role: role,
        content: content,
        timestamp: new Date().toISOString(),
        response_data: responseData,
        temp: true // Mark as temporary
      };
      
      // Add to local state even if DB fails
      setMessages(prev => [...prev, tempMessage]);
      
      // Don't show alert, just log error
      console.warn('Message saved locally but not persisted to database');
      return tempMessage;
    }
  };

  const handleResetClick = () => {
    setShowResetModal(true);
  };

  const handleConfirmReset = async () => {
    setIsClearing(true);
    try {
      // Use force version clear which includes version reset
      await forceVersionClear();
      
      // Clear application-specific data (fallback in case storage clearing missed anything)
      try {
        await chatService.clearAllMessages();
      } catch (dbError) {
        // Silent error handling
      }
      
      // Generate new session UUID and reset state
      const newUuid = uuidv4();
      setChatUuid(newUuid);
      
      // Reset all component state
      setMessages([]);
      setInputMessage('');
      setSelectedImage(null);
      setSelectedFile(null);
      setLastResponse(null);
      setSelectedGender(null);
      setBudget(null);
      setStylePreference('women');
      setShowBudgetInput(true);
      setIntentChipData(null);
      
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      setShowResetModal(false);
    } catch (error) {
      // Still try to reset the session even if storage clearing failed
      const newUuid = uuidv4();
      setChatUuid(newUuid);
      setMessages([]);
      setInputMessage('');
      setSelectedImage(null);
      setSelectedFile(null);
      setLastResponse(null);
      setSelectedGender(null);
      setBudget(null);
      setStylePreference('women');
      setShowBudgetInput(true);
      setIntentChipData(null);
      
      setShowResetModal(false);
    } finally {
      setIsClearing(false);
    }
  };

  const handleSendMessage = async (messageText = null) => {
    // Ensure messageText is string or null (not an event object)
    if (messageText && typeof messageText === 'object') {
      messageText = null;
    }
    
    // Use provided text or current input message
    const textToSend = messageText || inputMessage.trim();
    
    // At least image OR text is required
    if (!selectedFile && !textToSend) {
      alert('Please either upload an image or enter a text description.');
      return;
    }
    
    // Track message sending
    if (selectedFile && textToSend) {
      tracking.messageSent('image_and_text');
    } else if (selectedFile) {
      tracking.messageSent('image');
    } else {
      tracking.messageSent('text');
    }
    
    // Track chat start if this is the first message
    if (messages.length === 0) {
      tracking.chatStarted();
    }

    // Store current values before clearing
    const imageToSend = selectedImage;
    const fileToSend = selectedFile;

    // Clear input immediately if using current input message
    if (!messageText && inputMessage.trim()) {
      setInputMessage('');
    }

    // Clear image immediately when sending
    if (selectedImage) {
      setSelectedImage(null);
      setSelectedFile(null);
      // Reset file input to allow same file upload again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }

    setIsLoading(true);

    try {
      // Add user messages to chat
      if (imageToSend) {
        await addMessage('user', imageToSend, chatUuid, null); // Show image in chat
      }
      if (textToSend) {
        await addMessage('user', textToSend, chatUuid, null); // Show text in chat
      }

      // Collect last 5 user and bot messages for context
      const userMessages = messages.filter(m => m.role === 'user').slice(-5);
      const botMessages = messages.filter(m => m.role === 'assistant').slice(-5);
      
      // Extract images from chat history and create references
      const chatHistoryImages = {};
      let imageCounter = 1;
      
      const processedUserMessages = userMessages.map(m => {
        // Check if content is a base64 image
        if (m.content && m.content.startsWith('data:image')) {
          const imageKey = `chathistoryimage${imageCounter}`;
          chatHistoryImages[imageKey] = m.content;
          imageCounter++;
          return {
            content: imageKey, // Replace image data with reference
            timestamp: m.timestamp,
            message_index: m.message_index
          };
        }
        return {
          content: m.content,
          timestamp: m.timestamp,
          message_index: m.message_index
        };
      });
      
      const chatHistory = {
        user_messages: processedUserMessages,
        bot_messages: botMessages.map(m => ({
          content: m.content,
          timestamp: m.timestamp,
          message_index: m.message_index,
          response_data: m.response_data || null
        }))
      };

      console.log('Sending chat history:', chatHistory);
      console.log('Chat history images:', Object.keys(chatHistoryImages));

      // Transform chip data to preferences format
      const userPreferences = transformChipDataToPreferences(intentChipData);
      
      // Call the API
      const response = await apiService.recommendApparelWithText(
        fileToSend, 
        textToSend || undefined,
        chatUuid,
        budget,
        chatHistory,
        chatHistoryImages,
        selectedGender,
        userPreferences
      );

      console.log('Unified API Response:', response);
      console.log('Response Type:', response.response_type);
      console.log('Status:', response.status);
      console.log('Sections:', response.data?.sections);
      
      // Handle different response types with unified structure
      let botResponse = '';
      
      switch (response.response_type) {
        case 'GENERAL':
          botResponse = response.data?.message || 'Hello! How can I help you today?';
          break;
        case 'COMPLEMENT':
          botResponse = 'Here are my style recommendations to complete your outfit! 🛍️';
          break;
        case 'SEARCH':
          botResponse = `Found ${response.data?.sections?.[0]?.total_results || 'some'} options for "${response.metadata?.original_query}"! 👗`;
          break;
        case 'AMBIGUOUS':
          botResponse = "I need a bit more clarity to help you better. Please choose what you're looking for:";
          break;
        default:
          if (response.status === 'error') {
            botResponse = response.error || 'Sorry, I encountered an error. Please try again.';
          } else {
            botResponse = 'Here are my recommendations for you! 🛍️';
          }
      }

      const botMessage = await addMessage('assistant', botResponse, chatUuid, response);
      
      // Track message received
      tracking.messageReceived();
      
      // Store the unified response data associated with this message
      if (botMessage) {
        setLastResponse({ 
          ...response, 
          messageId: botMessage.id
        });
      }

      // Inputs already cleared above

    } catch (error) {
      console.error('API Error:', error);
      const errorResponse = {
        response_type: 'GENERAL',
        status: 'error',
        data: { sections: [], clarification_options: [] },
        error: error.message,
        metadata: {
          processing_time_seconds: 0,
          original_query: textToSend || 'image upload',
          detected_items: [],
          search_query: ''
        }
      };
      await addMessage('assistant', `Sorry, I encountered an error. Please try again.`, chatUuid, errorResponse);
      
      // Log error but don't show alert
      console.error('API Error:', error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = (file) => {
    if (!file) return;

    // Validate file type (JPEG, PNG, WebP only)
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please upload a JPEG, PNG, or WebP image file');
      return;
    }

    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      alert('File size too large. Please use an image under 10MB.');
      return;
    }

    // Store both the File object and preview URL
    setSelectedFile(file);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setSelectedImage(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };




  const handleBudgetSet = (budgetValue, style, intent) => {
    setBudget(budgetValue);
    setStylePreference(style);
    setShowBudgetInput(false);
    
    // Set the selected gender to match the style preference
    setSelectedGender(style === 'women' ? 'women' : 'men');
    
    // Store budget and style in localStorage for persistence
    localStorage.setItem('userBudget', budgetValue.toString());
    localStorage.setItem('userStyle', style);
    
    // Track preferences submission
    tracking.preferencesSubmitted({ budget: budgetValue, style, intent });
    tracking.budgetSet(budgetValue);
    tracking.styleSelected(style);
    
    // Show intent chips screen if user chose context-rich flow
    if (intent === 'context') {
      setShowIntentChips(true);
    }
    
    // Log the user's intent choice for future use
    console.log('User selected intent:', intent);
  };

  const handleBudgetEdit = () => {
    setShowBudgetInput(true);
  };

  const handleChipsContinue = (chipData) => {
    setIntentChipData(chipData);
    setShowIntentChips(false);
    
    // Store chip data in localStorage
    localStorage.setItem('intentChipData', JSON.stringify(chipData));
    
    // Track intent chip selections
    tracking.trackEvent('intent_chips_completed', chipData);
    
    console.log('Intent chips data:', chipData);
  };

  const handleChipsSkip = () => {
    setShowIntentChips(false);
    console.log('User skipped intent chips');
  };

  const transformChipDataToPreferences = (chipData) => {
    if (!chipData) return null;
    
    // Combine vibes and custom vibes into moods and vibes arrays
    const allVibes = [...(chipData.vibes || [])];
    if (chipData.customVibes && chipData.customVibes.length > 0) {
      allVibes.push(...chipData.customVibes);
    }
    
    // Convert single occasion to array format
    const occasions = chipData.occasion ? [chipData.occasion] : [];
    
    // Create preferences object in required format
    const preferences = {
      moods: allVibes,
      vibes: allVibes, // Same as moods for now
      occasions: occasions
    };
    
    // Only return preferences if there's actual data
    if (allVibes.length === 0 && occasions.length === 0) {
      return null;
    }
    
    return preferences;
  };

  // Load budget, style, and chip data from localStorage on mount
  useEffect(() => {
    const savedBudget = localStorage.getItem('userBudget');
    const savedStyle = localStorage.getItem('userStyle');
    const savedChipData = localStorage.getItem('intentChipData');
    
    if (savedBudget) {
      setBudget(parseInt(savedBudget));
      setShowBudgetInput(false);
    }
    
    if (savedStyle) {
      setStylePreference(savedStyle);
      // Also set the selected gender to match the style preference
      setSelectedGender(savedStyle === 'women' ? 'women' : 'men');
    }
    
    if (savedChipData) {
      try {
        setIntentChipData(JSON.parse(savedChipData));
      } catch (error) {
        console.error('Error parsing saved chip data:', error);
      }
    }
  }, []);

  return (
    <>
      {/* Preferences Section - Only show when setting budget initially */}
      {showBudgetInput && (
        <Preferences
          onBudgetSet={handleBudgetSet}
          currentBudget={budget}
          currentStyle={stylePreference}
          isVisible={true}
          showBudgetInput={showBudgetInput}
          onEdit={handleBudgetEdit}
        />
      )}

      {/* Intent Chips Screen - Only show for context intent after budget set */}
      {showIntentChips && (
        <IntentChipsScreen
          onContinue={handleChipsContinue}
          onSkip={handleChipsSkip}
          initialData={intentChipData}
        />
      )}

      {/* Main Chat Interface - Only show after budget is set and chips are done */}
      <AnimatePresence mode="wait">
        {!showBudgetInput && !showIntentChips && budget && (
          <Box className="bg-pink-950" minH="80vh">
            <VStack spacing={0} align="stretch" maxW="4xl" mx="auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
                className="w-full"
              >
                <Box className="bg-white rounded-3xl shadow-2xl mx-4 md:mx-8 overflow-hidden border border-stone-200 max-w-4xl mx-auto">
                  
                  {/* Budget/Style Header */}
                  <ChatBudgetHeader 
                    budget={budget}
                    style={stylePreference}
                    onEdit={handleBudgetEdit}
                    onReset={handleResetClick}
                  />

                  {/* Input Section - Only show if no messages */}
                  {messages.length === 0 && (
                    <ChatInput
                      budget={budget}
                      inputMessage={inputMessage}
                      setInputMessage={setInputMessage}
                      onSendMessage={handleSendMessage}
                      onFileUpload={handleFileUpload}
                      selectedImage={selectedImage}
                      onRemoveImage={() => {
                        setSelectedImage(null);
                        setSelectedFile(null);
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                      }}
                      isLoading={isLoading}
                    />
                  )}

                  {/* Welcome/Examples Section - Only show if no messages */}
                  {messages.length === 0 && (
                    <ChatWelcome 
                      style={stylePreference}
                      onExampleClick={(text) => {
                        // Set the selected gender based on style preference to prevent modal
                        if (!selectedGender) {
                          setSelectedGender(stylePreference === 'women' ? 'women' : 'men');
                        }
                        // Send the message immediately with the provided text
                        handleSendMessage(text);
                      }}
                      onPhotoUpload={() => {
                        // Set the selected gender based on style preference
                        if (!selectedGender) {
                          setSelectedGender(stylePreference === 'women' ? 'women' : 'men');
                        }
                        // Trigger the file input
                        fileInputRef.current?.click();
                      }}
                    />
                  )}

                  {/* Chat Messages Area - Only show if there are messages */}
                  {messages.length > 0 && (
                    <Box
                      ref={messageAreaRef}
                      className="bg-gradient-to-br from-pink-50 to-rose-50"
                      maxH="70vh"
                      overflowY="auto"
                      px={4}
                      py={4}
                    >
                      <VStack spacing={0} align="stretch">
                        {messages.map((message) => {
                          const productData = message.role === 'assistant' ? 
                            (message.response_data || (lastResponse && lastResponse.messageId === message.id ? lastResponse : null)) : 
                            null;
                          return (
                            <ChatBubble
                              key={message.id}
                              message={message}
                              productData={productData}
                            />
                          );
                        })}
                        {isLoading && (
                          <ChatBubble
                            message={{
                              content: '___LOADING___',
                              timestamp: new Date().toISOString(),
                              role: 'assistant',
                              message_index: -1
                            }}
                          />
                        )}
                        <div ref={messagesEndRef} />
                      </VStack>
                    </Box>
                  )}

                  {/* Input Section - Only show if there are messages */}
                  {messages.length > 0 && (
                    <ChatInput
                      budget={budget}
                      inputMessage={inputMessage}
                      setInputMessage={setInputMessage}
                      onSendMessage={handleSendMessage}
                      onFileUpload={handleFileUpload}
                      selectedImage={selectedImage}
                      onRemoveImage={() => {
                        setSelectedImage(null);
                        setSelectedFile(null);
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                      }}
                      isLoading={isLoading}
                    />
                  )}


                </Box>
              </motion.div>
            </VStack>
          </Box>
        )}
      </AnimatePresence>

      {/* Hidden File Input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/jpeg,image/png,image/webp"
        style={{ display: 'none' }}
      />


      {/* Reset Confirmation Modal */}
      {showResetModal && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="blackAlpha.600"
          zIndex={1000}
          display="flex"
          alignItems="center"
          justifyContent="center"
          p={4}
        >
          <Box
            bg="white"
            borderRadius="lg"
            boxShadow="xl"
            p={6}
            maxW="md"
            w="full"
            mx={4}
          >
            <Text fontSize="lg" fontWeight="bold" mb={4}>
              Clear Chat?
            </Text>
            <Text color="gray.600" mb={6}>
              This will erase your current conversation and start fresh. Are you sure you want to continue?
            </Text>
            <HStack spacing={3} justify="flex-end">
              <Button
                variant="ghost"
                onClick={() => setShowResetModal(false)}
                minH="44px"
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleConfirmReset}
                minH="44px"
                isLoading={isClearing}
                loadingText="Clearing..."
              >
                🗑️ Clear and Start New
              </Button>
            </HStack>
          </Box>
        </Box>
      )}
    </>
  );
};

export default ChatInterface;